import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Badge,
  useTheme,
  useMediaQuery,
  Collapse,
  Menu,
  MenuItem,
  Divider,
  Button,
  Popover,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Logout as LogoutIcon,
  BarChart as BarChartIcon,
  Security as SecurityIcon,
  Warning as WarningIcon,
  ExpandLess,
  ExpandMore,
  Assessment as SystemStatusIcon,
  Speed as CarrierIcon,
  Article as LogsIcon,
  History as ChangelogIcon,
  Settings as SettingsIcon,
  SignalCellular4Bar as MobileIcon,
  AccountCircle as AccountCircleIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Notifications as NotificationsIcon,
  Public as PublicIcon,
  ShoppingCart as ShoppingCartIcon,
  Category as CategoryIcon,
  Inventory as InventoryIcon,
  LocalOffer as LocalOfferIcon,
  Summarize as ReportIcon,
  Assignment as AssignmentIcon,
  Checklist as TasksListIcon,
  ConfirmationNumber as TicketsIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Build as BuildIcon,
  NetworkCheck as NetworkCheckIcon,
  Router as RouterIcon,
  MenuBook as KnowledgeBaseIcon,
  Map as MapIcon,
  AttachMoney as MoneyIcon,
  Campaign as MarketingIcon,
  Star as StarIcon,
  Extension as ExtensionIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import Footer from './Footer';
import { SONIC_APP_FONT_FAMILY } from '../theme/appFontFamily';

const drawerWidthExpanded = 240;
const drawerWidthCollapsed = 72;

/** Shared typography for expanded drawer nav labels (parent + leaf + sub-items). */
const drawerNavPrimaryTypographyProps = {
  variant: 'body2' as const,
  sx: { fontSize: '0.8125rem', lineHeight: 1.35, fontFamily: SONIC_APP_FONT_FAMILY },
};

const Layout: React.FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [subMenuAnchor, setSubMenuAnchor] = useState<{ el: HTMLElement; group: string } | null>(null);
  const [crmOpen, setCrmOpen] = useState(false);
  const [outagesOpen, setOutagesOpen] = useState(false);
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [systemStatusOpen, setSystemStatusOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [marketingOpen, setMarketingOpen] = useState(false);
  const [kbOpen, setKbOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [notificationsAnchor, setNotificationsAnchor] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<
    {
      id: string;
      type: string;
      title: string;
      body: string | null;
      case_id: string | null;
      support_desk_ticket_id?: string | null;
      task_id?: string | null;
      read_at: string | null;
      created_at: string;
    }[]
  >([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [sydneyDateLabel, setSydneyDateLabel] = useState('');
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Sydney date in header (e.g. "Tuesday, 17th March 2026")
  useEffect(() => {
    const formatSydneyDate = () => {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-AU', {
        timeZone: 'Australia/Sydney',
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
      const parts = formatter.formatToParts(now);
      const weekday = parts.find((p) => p.type === 'weekday')?.value ?? '';
      const day = parts.find((p) => p.type === 'day')?.value ?? '';
      const month = parts.find((p) => p.type === 'month')?.value ?? '';
      const year = parts.find((p) => p.type === 'year')?.value ?? '';
      const d = parseInt(day, 10);
      const ord = d >= 4 && d <= 20 ? 'th' : ['st', 'nd', 'rd'][(d % 10) - 1] || 'th';
      setSydneyDateLabel(`${weekday}, ${day}${ord} ${month} ${year}`);
    };
    formatSydneyDate();
    const t = setInterval(formatSydneyDate, 60000);
    return () => clearInterval(t);
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { count } = await apiService.getNotificationsUnreadCount();
      setUnreadCount(count);
    } catch {
      setUnreadCount(0);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchUnreadCount();
    const t = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(t);
  }, [user, fetchUnreadCount]);

  const handleNotificationsOpen = async (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchor(event.currentTarget);
    try {
      const list = await apiService.getNotifications(30, false);
      setNotifications(list);
    } catch {
      setNotifications([]);
    }
  };

  const handleNotificationsClose = () => setNotificationsAnchor(null);

  const handleNotificationClick = async (n: {
    id: string;
    case_id: string | null;
    support_desk_ticket_id?: string | null;
    task_id?: string | null;
    order_id?: string | null;
    read_at: string | null;
  }) => {
    if (n.case_id) navigate(`/crm/cases/${n.case_id}`);
    else if (n.support_desk_ticket_id) navigate(`/crm/support-desk/${n.support_desk_ticket_id}`);
    else if (n.task_id) navigate(`/crm/tasks/${n.task_id}`);
    else if (n.order_id) navigate('/admin/orders');
    if (!n.read_at) {
      try {
        await apiService.markNotificationRead(n.id, true);
        setUnreadCount((c) => Math.max(0, c - 1));
        setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)));
      } catch {}
    }
    handleNotificationsClose();
  };

  const handleMarkAllRead = async () => {
    try {
      await apiService.markAllNotificationsRead();
      setUnreadCount(0);
      setNotifications((prev) => prev.map((x) => ({ ...x, read_at: x.read_at || new Date().toISOString() })));
    } catch {}
  };

  const handleClearNotifications = async () => {
    try {
      await apiService.clearNotifications();
      setNotifications((prev) => prev.filter((x) => !x.read_at));
      fetchUnreadCount();
    } catch {}
  };

  const formatNotificationTime = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now.getTime() - d.getTime()) / 60000;
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${Math.floor(diff)}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return d.toLocaleDateString();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleUserMenuClose();
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleProfileClick = () => {
    navigate('/profile');
    handleUserMenuClose();
    if (isMobile) setMobileOpen(false);
  };

  const handleMyNotificationsClick = () => {
    navigate('/my-notifications');
    handleUserMenuClose();
    if (isMobile) setMobileOpen(false);
  };

  const menuItems: Array<{
    text: string;
    icon: React.ReactNode;
    path?: string;
    exact?: boolean;
    isCollapsible?: boolean;
    group?: string;
  }> = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/', exact: true },
    { text: 'CRM', icon: <PeopleIcon />, isCollapsible: true, group: 'crm' },
    { text: 'Outages', icon: <WarningIcon />, isCollapsible: true, group: 'outages' },
    { text: 'Orders', icon: <ShoppingCartIcon />, isCollapsible: true, group: 'orders' },
    { text: 'Reports', icon: <ReportIcon />, isCollapsible: true, group: 'reports' },
    { text: 'Tools', icon: <BuildIcon />, isCollapsible: true, group: 'tools' },
    { text: 'Marketing', icon: <MarketingIcon />, isCollapsible: true, group: 'marketing' },
    { text: 'Knowledge Base', icon: <KnowledgeBaseIcon />, isCollapsible: true, group: 'kb' },
    { text: 'Settings', icon: <SettingsIcon />, isCollapsible: true, group: 'settings' },
    { text: 'System Status', icon: <SystemStatusIcon />, isCollapsible: true, group: 'system' }
  ];

  const crmItems = [
    { text: 'Accounts', icon: <PeopleIcon />, path: '/accounts' },
    { text: 'Cases', icon: <AssignmentIcon />, path: '/crm/cases' },
    { text: 'Tasks', icon: <TasksListIcon />, path: '/crm/tasks' },
    { text: 'Support Desk', icon: <TicketsIcon />, path: '/crm/support-desk' },
  ];

  const outageItems = [
    { text: 'Internet Outages', icon: <WarningIcon />, path: '/internet-outages' },
    { text: 'Mobile Outages', icon: <MobileIcon />, path: '/mobile-outages' },
    { text: 'Voice Outages', icon: <WarningIcon />, path: '/voice-outages' },
    { text: 'Web Outages', icon: <PublicIcon />, path: '/web-services-outages' },
  ];

  const orderItems = [
    { text: 'Orders', icon: <ShoppingCartIcon />, path: '/admin/orders' },
    { text: 'Products', icon: <InventoryIcon />, path: '/admin/products' },
    { text: 'Hardware', icon: <InventoryIcon />, path: '/admin/hardware' },
    { text: 'Add-ons', icon: <ExtensionIcon />, path: '/admin/addons' },
    { text: 'Categories', icon: <CategoryIcon />, path: '/admin/categories' },
    { text: 'Coupons', icon: <LocalOfferIcon />, path: '/admin/coupons' },
  ];

  const reportItems = [
    { text: 'Services', icon: <BarChartIcon />, path: '/reports/services' },
    { text: 'Services Map', icon: <MapIcon />, path: '/reports/services-map' },
    { text: 'Internet Services', icon: <BarChartIcon />, path: '/reports/internet-services' },
    { text: 'Domain Names', icon: <PublicIcon />, path: '/reports/domain-names' },
    // Financial reports expose cost and margin: admins only
    ...(user?.is_admin ? [{ text: 'Internet Financial', icon: <MoneyIcon />, path: '/reports/internet-financial' }] : []),
    ...(user?.is_admin ? [{ text: 'Mobile Financial', icon: <MoneyIcon />, path: '/reports/mobile-financial' }] : []),
  ];

  const toolsItems = [
    { text: 'Service Qualification', icon: <NetworkCheckIcon />, path: '/tools/service-qualification' },
    { text: 'RADIUS Lookup', icon: <RouterIcon />, path: '/tools/radius-lookup' },
  ];

  const marketingItems = [
    { text: 'Google Reviews', icon: <StarIcon />, path: '/marketing/google-reviews' },
  ];

  // Helper function to check if a path matches (handles sub-routes)
  const isPathActive = useCallback((itemPath: string, currentPath: string, exact?: boolean) => {
    if (exact) {
      return currentPath === itemPath;
    }
    return currentPath === itemPath || currentPath.startsWith(itemPath + '/');
  }, []);

  const kbItems = [
    { text: 'Browse Articles', icon: <KnowledgeBaseIcon />, path: '/knowledge-base' },
    { text: 'New Article', icon: <KnowledgeBaseIcon />, path: '/knowledge-base/new' },
  ];

  const settingsItems = [
    { text: 'System Settings', icon: <SettingsIcon />, path: '/settings' },
    { text: 'Import Data', icon: <UploadIcon />, path: '/import' },
    { text: 'Export Data', icon: <DownloadIcon />, path: '/export' },
  ];

  const systemStatusItems = [
    { text: 'Feed Status', icon: <CarrierIcon />, path: '/system/feed-status' },
    { text: 'System Logs', icon: <LogsIcon />, path: '/system/logs' },
    { text: 'Changelog', icon: <ChangelogIcon />, path: '/system/changelog' },
  ];

  // Open the section that owns the current route (e.g. deep link / refresh); accordion — at most one open.
  useEffect(() => {
    const path = location.pathname;
    let match: string | null = null;
    if (crmItems.some((s) => s.path && isPathActive(s.path, path))) match = 'crm';
    else if (outageItems.some((s) => s.path && isPathActive(s.path, path))) match = 'outages';
    else if (orderItems.some((s) => s.path && isPathActive(s.path, path))) match = 'orders';
    else if (reportItems.some((s) => s.path && isPathActive(s.path, path))) match = 'reports';
    else if (toolsItems.some((s) => s.path && isPathActive(s.path, path))) match = 'tools';
    else if (marketingItems.some((s) => s.path && isPathActive(s.path, path))) match = 'marketing';
    else if (kbItems.some((s) => s.path && isPathActive(s.path, path))) match = 'kb';
    else if (settingsItems.some((s) => s.path && isPathActive(s.path, path))) match = 'settings';
    else if (systemStatusItems.some((s) => s.path && isPathActive(s.path, path))) match = 'system';

    setCrmOpen(match === 'crm');
    setOutagesOpen(match === 'outages');
    setOrdersOpen(match === 'orders');
    setReportsOpen(match === 'reports');
    setToolsOpen(match === 'tools');
    setMarketingOpen(match === 'marketing');
    setKbOpen(match === 'kb');
    setSettingsOpen(match === 'settings');
    setSystemStatusOpen(match === 'system');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nav paths are static; item arrays are new refs each render
  }, [location.pathname, isPathActive]);

  const getCollapseState = (group: string) => {
    switch (group) {
      case 'crm': return crmOpen;
      case 'outages': return outagesOpen;
      case 'orders': return ordersOpen;
      case 'reports': return reportsOpen;
      case 'tools': return toolsOpen;
      case 'marketing': return marketingOpen;
      case 'kb': return kbOpen;
      case 'settings': return settingsOpen;
      case 'system': return systemStatusOpen;
      default: return false;
    }
  };
  const setCollapseState = (group: string, open: boolean) => {
    switch (group) {
      case 'crm': setCrmOpen(open); break;
      case 'outages': setOutagesOpen(open); break;
      case 'orders': setOrdersOpen(open); break;
      case 'reports': setReportsOpen(open); break;
      case 'tools': setToolsOpen(open); break;
      case 'marketing': setMarketingOpen(open); break;
      case 'kb': setKbOpen(open); break;
      case 'settings': setSettingsOpen(open); break;
      case 'system': setSystemStatusOpen(open); break;
    }
  };

  const collapseAllNavGroups = () => {
    setCrmOpen(false);
    setOutagesOpen(false);
    setOrdersOpen(false);
    setReportsOpen(false);
    setToolsOpen(false);
    setMarketingOpen(false);
    setKbOpen(false);
    setSettingsOpen(false);
    setSystemStatusOpen(false);
  };

  /** Accordion: only one collapsible group open at a time in the expanded drawer. */
  const toggleNavGroup = (group: string) => {
    if (getCollapseState(group)) {
      setCollapseState(group, false);
    } else {
      collapseAllNavGroups();
      setCollapseState(group, true);
    }
  };
  const getSubItems = (group: string) => {
    switch (group) {
      case 'crm': return crmItems;
      case 'outages': return outageItems;
      case 'orders': return orderItems;
      case 'reports': return reportItems;
      case 'tools': return toolsItems;
      case 'marketing': return marketingItems;
      case 'kb': return kbItems;
      case 'settings': return settingsItems;
      case 'system': return systemStatusItems;
      default: return [];
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawerWidth = sidebarCollapsed ? drawerWidthCollapsed : drawerWidthExpanded;

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ justifyContent: sidebarCollapsed ? 'center' : 'flex-start', minHeight: 64, px: 2 }}>
        <Box
          component="img"
          src={sidebarCollapsed ? '/favicon.ico' : '/assets/sonic-logo-white.png'}
          alt="Sonic Internet & Voice"
          sx={{
            width: sidebarCollapsed ? 36 : 160,
            maxWidth: '100%',
            maxHeight: sidebarCollapsed ? 36 : 48,
            height: 'auto',
            objectFit: 'contain',
            objectPosition: 'left center',
          }}
        />
      </Toolbar>
      <List dense sx={{ flexGrow: 1, py: 0.5 }}>
        {menuItems.map((item) => (
          <React.Fragment key={item.text}>
            {item.isCollapsible ? (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={(e) => {
                      if (sidebarCollapsed) {
                        setSubMenuAnchor((prev) => (prev?.group === item.group ? null : { el: e.currentTarget, group: item.group! }));
                      } else {
                        item.group && toggleNavGroup(item.group);
                      }
                    }}
                    selected={item.group ? getSubItems(item.group).some((s) => s.path && isPathActive(s.path, location.pathname)) : false}
                    sx={{ justifyContent: sidebarCollapsed ? 'center' : 'flex-start', px: sidebarCollapsed ? 1 : 2, py: 0.75 }}
                  >
                    <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 40 }}>{item.icon}</ListItemIcon>
                    {!sidebarCollapsed && (
                      <>
                        <ListItemText primary={item.text} primaryTypographyProps={drawerNavPrimaryTypographyProps} />
                        {item.group &&
                          (getCollapseState(item.group) ? (
                            <ExpandLess sx={{ fontSize: '1.125rem', color: 'text.secondary' }} />
                          ) : (
                            <ExpandMore sx={{ fontSize: '1.125rem', color: 'text.secondary' }} />
                          ))}
                      </>
                    )}
                  </ListItemButton>
                </ListItem>
                {!sidebarCollapsed && (
                  <Collapse in={item.group ? getCollapseState(item.group) : false} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding dense>
                      {(item.group ? getSubItems(item.group) : []).map((subItem) => (
                        <ListItem key={subItem.text} disablePadding>
                          <ListItemButton
                            sx={{ pl: 3, py: 0.5 }}
                            selected={subItem.path ? isPathActive(subItem.path, location.pathname) : false}
                            onClick={() => {
                              navigate(subItem.path!);
                              if (isMobile) setMobileOpen(false);
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>{subItem.icon}</ListItemIcon>
                            <ListItemText primary={subItem.text} primaryTypographyProps={drawerNavPrimaryTypographyProps} />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                )}
              </>
            ) : (
              <ListItem disablePadding>
                <ListItemButton
                  selected={item.path ? isPathActive(item.path, location.pathname, item.exact) : false}
                  onClick={() => {
                    item.path && navigate(item.path);
                    if (isMobile) setMobileOpen(false);
                  }}
                  sx={{ justifyContent: sidebarCollapsed ? 'center' : 'flex-start', px: sidebarCollapsed ? 1 : 2, py: 0.75 }}
                >
                  <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 40 }}>{item.icon}</ListItemIcon>
                  {!sidebarCollapsed && (
                    <ListItemText primary={item.text} primaryTypographyProps={drawerNavPrimaryTypographyProps} />
                  )}
                </ListItemButton>
              </ListItem>
            )}
          </React.Fragment>
        ))}
      </List>
      <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
        <ListItemButton
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          sx={{ justifyContent: sidebarCollapsed ? 'center' : 'flex-start', py: 1.5 }}
        >
          <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 56, color: 'primary.main' }}>
            {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </ListItemIcon>
          {!sidebarCollapsed && (
            <ListItemText
              primary={sidebarCollapsed ? 'Expand' : 'Collapse'}
              primaryTypographyProps={drawerNavPrimaryTypographyProps}
            />
          )}
        </ListItemButton>
      </Box>
      <Popover
        open={Boolean(subMenuAnchor)}
        anchorEl={subMenuAnchor?.el}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
        onClose={() => setSubMenuAnchor(null)}
        PaperProps={{ sx: { ml: 0.5 } }}
        disableRestoreFocus
      >
        <List dense sx={{ minWidth: 180, py: 0.5 }}>
          {subMenuAnchor && getSubItems(subMenuAnchor.group).map((subItem) => (
            <ListItem key={subItem.text} disablePadding>
              <ListItemButton
                selected={subItem.path ? isPathActive(subItem.path, location.pathname) : false}
                onClick={() => {
                  navigate(subItem.path!);
                  setSubMenuAnchor(null);
                  if (isMobile) setMobileOpen(false);
                }}
                sx={{ py: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>{subItem.icon}</ListItemIcon>
                <ListItemText primary={subItem.text} primaryTypographyProps={drawerNavPrimaryTypographyProps} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Popover>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Sonic Operations Manager (SonOps)
          </Typography>

          {sydneyDateLabel && (
            <Typography variant="body2" component="span" sx={{ mr: 2, opacity: 0.95 }}>
              {sydneyDateLabel}
            </Typography>
          )}

          {/* Notifications bell */}
          <IconButton
            color="inherit"
            aria-label="Notifications"
            onClick={handleNotificationsOpen}
            sx={{ mr: 0.5 }}
          >
            <Badge badgeContent={unreadCount > 0 ? unreadCount : 0} color="secondary">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <Menu
            anchorEl={notificationsAnchor}
            open={Boolean(notificationsAnchor)}
            onClose={handleNotificationsClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { minWidth: 320, maxWidth: 400, maxHeight: 360 } }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2">Notifications</Typography>
              {(notifications.some((n) => !n.read_at) || notifications.some((n) => n.read_at)) && (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {notifications.some((n) => !n.read_at) && (
                    <Button size="small" onClick={handleMarkAllRead}>
                      Mark all read
                    </Button>
                  )}
                  <Button size="small" onClick={handleClearNotifications}>
                    Clear read
                  </Button>
                </Box>
              )}
            </Box>
            <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
              {notifications.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No notifications
                </Typography>
              )}
              {notifications.map((n) => (
                <MenuItem
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  sx={{ whiteSpace: 'normal', py: 1.5, bgcolor: n.read_at ? undefined : 'action.hover' }}
                >
                  <ListItemText
                    primary={n.title}
                    primaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.8125rem' } }}
                    secondary={
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem' }}>
                        {formatNotificationTime(n.created_at)}
                      </Typography>
                    }
                  />
                </MenuItem>
              ))}
            </Box>
          </Menu>
          
          {/* User Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={handleUserMenuOpen}
              sx={{ 
                color: 'inherit',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <AccountCircleIcon sx={{ mr: 0.5 }} />
              <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
                {user?.display_name || user?.username}
              </Typography>
              <ArrowDropDownIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
            
            <Menu
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={handleUserMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem disabled>
                <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {user?.display_name || user?.username}
                  </Typography>
                  {user?.email && (
                    <Typography variant="caption" color="text.secondary">
                      {user.email}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleProfileClick}>
                <ListItemIcon>
                  <SecurityIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Profile & Security</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleMyNotificationsClick}>
                <ListItemIcon>
                  <NotificationsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>My Notifications</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidthExpanded },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar />
        <Box sx={{ flexGrow: 1 }}>
          <Outlet />
        </Box>
        <Footer />
      </Box>
    </Box>
  );
};

export default Layout;
